import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    MenuItem,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import { useQuery } from '@tanstack/react-query';
import { albumService } from '../../api/albums';
import { GallerySort, GalleryTypeFilter, GalleryViewMode } from '../../types/entities/image.entity';

interface GalleryToolbarProps {
    typeFilter: GalleryTypeFilter;
    sort: GallerySort;
    viewMode: GalleryViewMode;
    title?: string;
    onTypeFilterChange: (value: GalleryTypeFilter) => void;
    onSortChange: (value: GallerySort) => void;
    onViewModeChange: (value: GalleryViewMode) => void;
    onUploadClick: () => void;
    onAddExistingClick?: () => void;
}

export const GalleryToolbar = ({
    typeFilter,
    sort,
    viewMode,
    title,
    onTypeFilterChange,
    onSortChange,
    onViewModeChange,
    onUploadClick,
    onAddExistingClick,
}: GalleryToolbarProps) => {
    const { t } = useTranslation();

    return (
        <Stack spacing={2} mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Typography variant="h4">{title ?? t('gallery.title')}</Typography>
                <Stack direction="row" spacing={1}>
                    {onAddExistingClick && (
                        <Button variant="outlined" onClick={onAddExistingClick}>
                            {t('gallery.addExistingPhotos')}
                        </Button>
                    )}
                    <Button variant="contained" onClick={onUploadClick}>
                        {t('gallery.addImage')}
                    </Button>
                </Stack>
            </Box>

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
            >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                        label={t('gallery.filterAll')}
                        color={typeFilter === 'all' ? 'primary' : 'default'}
                        onClick={() => onTypeFilterChange('all')}
                        variant={typeFilter === 'all' ? 'filled' : 'outlined'}
                    />
                    <Chip
                        label={t('gallery.filterPhotos')}
                        color={typeFilter === 'image' ? 'primary' : 'default'}
                        onClick={() => onTypeFilterChange('image')}
                        variant={typeFilter === 'image' ? 'filled' : 'outlined'}
                    />
                    <Chip
                        label={t('gallery.filterVideos')}
                        color={typeFilter === 'video' ? 'primary' : 'default'}
                        onClick={() => onTypeFilterChange('video')}
                        variant={typeFilter === 'video' ? 'filled' : 'outlined'}
                    />
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={sort}
                        onChange={(_event, value: GallerySort | null) => {
                            if (value) {
                                onSortChange(value);
                            }
                        }}
                    >
                        <ToggleButton value="newest">{t('gallery.sortNewest')}</ToggleButton>
                        <ToggleButton value="oldest">{t('gallery.sortOldest')}</ToggleButton>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={viewMode}
                        onChange={(_event, value: GalleryViewMode | null) => {
                            if (value) {
                                onViewModeChange(value);
                            }
                        }}
                    >
                        <ToggleButton value="grid" aria-label={t('gallery.viewGrid')}>
                            <GridViewIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="slider" aria-label={t('gallery.viewSlider')}>
                            <ViewCarouselIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
            </Stack>
        </Stack>
    );
};

interface GalleryUploadDialogProps {
    open: boolean;
    isUploading: boolean;
    defaultAlbumId?: string | null;
    onClose: () => void;
    onUpload: (files: File[], albumId?: string | null) => void;
}

export const GalleryUploadDialog = ({
    open,
    isUploading,
    defaultAlbumId,
    onClose,
    onUpload,
}: GalleryUploadDialogProps) => {
    const { t } = useTranslation();
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [fileError, setFileError] = useState('');
    const [albumId, setAlbumId] = useState<string>('');

    const { data: albumsData } = useQuery({
        queryKey: ['albums'],
        queryFn: () => albumService.getAll(1),
        enabled: open,
    });

    const albums = albumsData?.data.albums ?? [];

    const handleClose = () => {
        setSelectedFiles([]);
        setFileError('');
        setAlbumId('');
        onClose();
    };

    const handleOpen = () => {
        if (defaultAlbumId) {
            setAlbumId(defaultAlbumId);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (files.length > 0) {
            setSelectedFiles(files);
            setFileError('');
        }
    };

    const handleRemoveFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUpload = () => {
        if (selectedFiles.length === 0) {
            setFileError(t('validation.required'));
            return;
        }
        onUpload(selectedFiles, albumId || null);
        setSelectedFiles([]);
        setFileError('');
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleOpen }}>
            <DialogTitle>{t('gallery.uploadTitle')}</DialogTitle>
            <DialogContent>
                <TextField
                    select
                    margin="dense"
                    fullWidth
                    label={t('gallery.uploadAlbum')}
                    value={albumId}
                    onChange={(event) => setAlbumId(event.target.value)}
                    sx={{ mb: 2 }}
                >
                    <MenuItem value="">{t('gallery.noAlbum')}</MenuItem>
                    {albums.map((album) => (
                        <MenuItem key={album.id} value={album.id}>
                            {album.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    autoFocus
                    margin="dense"
                    type="file"
                    fullWidth
                    inputProps={{ multiple: true }}
                    onChange={handleFileSelect}
                    error={!!fileError}
                    helperText={fileError}
                    required
                />
                {selectedFiles.length > 0 && (
                    <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('gallery.selectedFiles')} ({selectedFiles.length}):
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {selectedFiles.map((file, index) => (
                                <Chip
                                    key={`${file.name}-${index}`}
                                    label={file.name}
                                    onDelete={() => handleRemoveFile(index)}
                                    size="small"
                                    sx={{ mb: 1 }}
                                />
                            ))}
                        </Stack>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{t('common.cancel')}</Button>
                <Button onClick={handleUpload} variant="contained" disabled={isUploading}>
                    {isUploading ? <CircularProgress size={20} /> : t('common.upload')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface GalleryCreateAlbumDialogProps {
    open: boolean;
    isSaving: boolean;
    onClose: () => void;
    onCreate: (name: string) => void;
}

export const GalleryCreateAlbumDialog = ({
    open,
    isSaving,
    onClose,
    onCreate,
}: GalleryCreateAlbumDialogProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [error, setError] = useState('');

    const handleClose = () => {
        setName('');
        setError('');
        onClose();
    };

    const handleCreate = () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setError(t('validation.required'));
            return;
        }
        onCreate(trimmed);
        setName('');
        setError('');
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>{t('gallery.createAlbumTitle')}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    fullWidth
                    label={t('gallery.albumName')}
                    value={name}
                    onChange={(event) => {
                        setName(event.target.value);
                        setError('');
                    }}
                    error={!!error}
                    helperText={error}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{t('common.cancel')}</Button>
                <Button onClick={handleCreate} variant="contained" disabled={isSaving}>
                    {isSaving ? <CircularProgress size={20} /> : t('common.create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface GalleryDeleteDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

export const GalleryDeleteDialog = ({ open, onClose, onConfirm }: GalleryDeleteDialogProps) => {
    const { t } = useTranslation();

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{t('gallery.deleteTitle')}</DialogTitle>
            <DialogContent>
                <Typography>{t('gallery.deleteConfirmation')}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button onClick={onConfirm} color="error" variant="contained">
                    {t('common.yes')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface GalleryRemoveFromAlbumDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

export const GalleryRemoveFromAlbumDialog = ({
    open,
    onClose,
    onConfirm,
}: GalleryRemoveFromAlbumDialogProps) => {
    const { t } = useTranslation();

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{t('gallery.removeFromAlbumTitle')}</DialogTitle>
            <DialogContent>
                <Typography>{t('gallery.removeFromAlbumConfirmation')}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button onClick={onConfirm} color="warning" variant="contained">
                    {t('common.yes')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
